import type { IconName } from './types'

export interface LegacyRailIconPresentation {
  /** Repo-vendored source path used to reproduce the legacy rail silhouette. */
  path: string
  viewBox: '0 0 24 24' | '0 0 512 512'
  size: number
  /** Fractional CSS box derived from the same 44px live semantic crop. */
  width?: number
  height?: number
  x?: number
  y?: number
  filled?: boolean
  strokeWidth?: number
  circle?: { cx: number; cy: number; r: number }
}

const VIEW_BOX = '0 0 512 512' as const

/**
 * v3 rendered these six Font Awesome Light glyphs at the 17.5px em produced by `text-sm fa-lg`.
 * Keeping that fractional CSS em reproduces their topology and optical box without loading Font Awesome
 * at runtime or scattering route-local offsets through navigation components.
 */
export const LEGACY_RAIL_ICON_PRESENTATION: Partial<Record<IconName, LegacyRailIconPresentation>> =
  {
    // Legacy uses Font Awesome Pro Light, whose licensed vector is not shipped in v4. Lucide
    // Settings is the closest license-compatible outline source (ISC):
    // https://unpkg.com/lucide-static@1.28.0/icons/settings.svg
    // Keep this override rail-only: generic `gear` callers retain the existing filled FA Free path.
    gear: {
      viewBox: '0 0 24 24',
      size: 21.875,
      filled: false,
      strokeWidth: 1.5,
      path: 'M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915',
      circle: { cx: 12, cy: 12, r: 3 },
    },
    'magnifying-glass-chart': {
      viewBox: VIEW_BOX,
      size: 17.5,
      x: -0.17,
      y: -0.2,
      path: 'M208 32C305 32 384 111 384 208C384 305 305 384 208 384C111 384 32 305 32 208C32 111 111 32 208 32ZM208 416C260 416 307 397 343 366L485 507C491 514 501 514 507 507C514 501 514 491 507 485L366 343C397 307 416 260 416 208C416 93 323 0 208 0C93 0 0 93 0 208C0 323 93 416 208 416ZM128 208V272C128 281 135 288 144 288C153 288 160 281 160 272V208C160 199 153 192 144 192C135 192 128 199 128 208ZM192 112V272C192 281 199 288 208 288C217 288 224 281 224 272V112C224 103 217 96 208 96C199 96 192 103 192 112ZM256 176V272C256 281 263 288 272 288C281 288 288 281 288 272V176C288 167 281 160 272 160C263 160 256 167 256 176Z',
    },
    'circle-nodes': {
      viewBox: VIEW_BOX,
      size: 17.5,
      height: 18,
      x: -0.15,
      path: 'M376 122C383 126 391 128 400 128C403 128 405 128 408 127C431 124 448 104 448 80C448 54 427 32 400 32C374 32 352 54 352 80C352 81 352 81 352 82C353 93 357 104 364 112C367 116 372 119 376 122ZM370 154C355 148 342 138 333 124L159 194C160 198 160 203 160 208C160 222 156 236 150 248L288 368C301 359 317 352 334 352L370 154ZM366 358C395 370 416 398 416 432C416 476 380 512 336 512C292 512 256 476 256 432C256 418 260 404 267 392L129 272C115 282 98 288 80 288C36 288 0 252 0 208C0 164 36 128 80 128C108 128 133 142 147 164L321 94C321 90 320 85 320 80C320 36 356 0 400 0C444 0 480 36 480 80C480 124 445 159 402 160L366 358ZM120 235C121 234 121 233 122 232C126 225 128 217 128 208C128 181 107 160 80 160C54 160 32 181 32 208C32 234 54 256 80 256C89 256 97 254 104 250C110 246 116 241 120 235ZM291 414C289 420 288 426 288 432C288 459 310 480 336 480C363 480 384 459 384 432C384 406 363 384 336 384C322 384 309 390 301 400C298 402 296 406 294 409C293 411 292 413 291 414Z',
    },
    'folder-image': {
      viewBox: VIEW_BOX,
      size: 17.5,
      width: 17.5,
      x: -0.14,
      y: 0.94,
      path: 'M480 384C480 402 466 416 448 416H64C46 416 32 402 32 384V96C32 78 46 64 64 64H203C210 64 216 66 222 70L260 99C271 108 285 112 299 112H448C466 112 480 126 480 144V384ZM64 448H448C483 448 512 419 512 384V144C512 109 483 80 448 80H299C292 80 285 78 280 74L241 45C230 37 217 32 203 32H64C29 32 0 61 0 96V384C0 419 29 448 64 448ZM184 160C184 147 173 136 160 136C147 136 136 147 136 160C136 173 147 184 160 184C173 184 184 173 184 160ZM231 203L137 298C131 304 128 312 128 320C128 338 142 352 160 352H352C370 352 384 338 384 320C384 312 381 304 375 298L281 203C274 196 265 192 256 192C247 192 238 196 231 203ZM256 224C257 224 258 224 258 225L352 320H160L254 225C254 224 255 224 256 224Z',
    },
    'chart-line-up': {
      viewBox: VIEW_BOX,
      size: 17.5,
      width: 16.8,
      height: 17.7,
      x: -1.48,
      y: 0.86,
      path: 'M32 48C32 39 25 32 16 32C7 32 0 39 0 48V400C0 444 36 480 80 480H496C505 480 512 473 512 464C512 455 505 448 496 448H80C54 448 32 427 32 400V48ZM320 144C320 153 327 160 336 160H425L320 265L235 181C229 175 219 175 213 181L101 293C95 299 95 309 101 315C107 322 117 322 123 315L224 215L309 299C315 306 325 306 331 299L448 183V272C448 281 455 288 464 288C473 288 480 281 480 272V144C480 135 473 128 464 128H336C327 128 320 135 320 144Z',
    },
    'rocket-launch': {
      viewBox: VIEW_BOX,
      size: 17.5,
      height: 18,
      x: -0.55,
      y: 0.15,
      path: 'M504 21C503 15 498 10 492 8C447-1 380-6 316 27C279 47 243 78 214 128H124C107 128 91 137 82 151L30 240C17 261 32 288 57 288H160C196 288 224 317 224 352V456C224 480 252 496 273 483L361 430C376 421 384 406 384 389V298C434 269 466 234 485 196C518 132 513 65 504 21ZM352 315V389C352 394 350 400 345 403L256 455V352C256 351 256 350 256 349C293 339 325 328 352 315ZM250 317C240 292 220 272 195 262C226 141 278 83 331 56C381 30 434 31 475 38C481 78 483 131 457 182C430 234 371 287 250 317ZM164 256C163 256 162 256 160 256H57L110 168C113 163 118 160 124 160H197C184 187 173 219 164 256ZM368 168C355 168 344 157 344 144C344 131 355 120 368 120C382 120 392 131 392 144C392 157 382 168 368 168ZM312 144C312 175 337 200 368 200C399 200 424 175 424 144C424 113 399 88 368 88C337 88 312 113 312 144ZM130 451C121 460 104 468 83 473C66 477 48 479 33 480C34 465 35 447 39 429C44 408 52 392 62 382C81 363 111 363 130 382C149 401 149 432 130 451ZM153 474C184 442 184 391 153 360C121 328 70 328 39 360C8 391 1 452 1 486C0 501 12 512 26 512C61 511 122 505 153 474Z',
    },
    user: {
      viewBox: VIEW_BOX,
      size: 17.5,
      x: -0.2,
      y: -0.5,
      path: 'M160 128C160 75 203 32 256 32C309 32 352 75 352 128C352 181 309 224 256 224C203 224 160 181 160 128ZM384 128C384 57 327 0 256 0C185 0 128 57 128 128C128 199 185 256 256 256C327 256 384 199 384 128ZM64 480C64 401 129 336 208 336H304C384 336 448 401 448 480V496C448 505 455 512 464 512C473 512 480 505 480 496V480C480 383 401 304 304 304H208C111 304 32 383 32 480V496C32 505 39 512 48 512C57 512 64 505 64 496V480Z',
    },
  }

export function legacyRailIconSize(name: IconName): number | undefined {
  return LEGACY_RAIL_ICON_PRESENTATION[name]?.size
}
